<!-- mettal-3\src\components\VideoPlayer.vue -->
<template>
	<div
		ref="playerShell"
		class="vp-custom-player-shell"
		:style="shellPlaceholderStyle"
	>
	<teleport to="body" :disabled="!isEmbeddedFullscreen">
	<div
		ref="playerWrapper"
		class="vp-custom-player-wrapper"
		:class="{ 'vp-custom-player-wrapper--embedded-fullscreen': isEmbeddedFullscreen }"
	>
		<!-- Видеоплеер -->
		<video
			ref="videoElement"
			class="vp-custom-video-element"
			:poster="posterImage"
			:src="videoSource"
			playsinline
			controlslist="nodownload noplaybackrate noremoteplayback"
			disablepictureinpicture
			disableremoteplayback
			@click="togglePlay"
			@contextmenu.prevent
			@timeupdate="updateProgress"
			@loadedmetadata="onVideoLoaded"
			@play="onPlay"
			@pause="onPause"
			@ended="onVideoEnded"
			@error="onVideoError"
		>
			Ваш браузер не поддерживает видео.
		</video>

		<!-- Кастомные контролы -->
		<div class="vp-custom-controls-overlay" :class="{ 'vp-custom-controls-visible': showControls }">
			<!-- Прогресс-бар -->
			<div class="vp-custom-progress-wrapper" @click="seek">
				<div class="vp-custom-progress-track">
					<div
						class="vp-custom-progress-fill"
						:style="{ width: progressPercent + '%' }"
					></div>
					<div
						class="vp-custom-progress-thumb"
						:style="{ left: progressPercent + '%' }"
					></div>
				</div>
			</div>

			<!-- Нижняя панель управления -->
			<div class="vp-custom-controls-panel">
				<!-- Левая группа: Play/Pause, Время -->
				<div class="vp-custom-controls-left-group">
					<button type="button" class="vp-custom-btn vp-custom-play-pause-btn" @click="togglePlay">
						<span v-if="isPlaying" class="vp-custom-icon">⏸</span>
						<span v-else class="vp-custom-icon">▶</span>
					</button>
					<div class="vp-custom-time-info">
						<span class="vp-custom-current-time">{{ formatTime(currentTime) }}</span>
						<span class="vp-custom-time-divider">/</span>
						<span class="vp-custom-total-duration">{{ formatTime(duration) }}</span>
					</div>
				</div>

				<!-- Правая группа: Громкость, Полноэкранный режим -->
				<div class="vp-custom-controls-right-group">
					<!-- Контрол громкости -->
					<div class="vp-custom-volume-section">
						<button type="button" class="vp-custom-btn vp-custom-volume-btn" @click="toggleMute">
							<span v-if="isMuted || volume === 0" class="vp-custom-icon">🔇</span>
							<span v-else-if="volume < 0.5" class="vp-custom-icon">🔉</span>
							<span v-else class="vp-custom-icon">🔊</span>
						</button>
						<div class="vp-custom-volume-slider-wrapper">
							<input
								type="range"
								class="vp-custom-volume-range"
								min="0"
								max="100"
								:value="volume * 100"
								@input="changeVolume"
							/>
						</div>
					</div>

					<!-- Кнопка полноэкранного режима -->
					<button type="button" class="vp-custom-btn vp-custom-fullscreen-btn" @click="toggleFullscreen">
						<span v-if="isFullscreen" class="vp-custom-icon">⛶</span>
						<span v-else class="vp-custom-icon">⛶</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Центральная кнопка Play (показывается при паузе) -->
		<div
			v-if="!isPlaying && !isLoading"
			class="vp-custom-center-play-overlay"
			@click="togglePlay"
		>
			<div class="vp-custom-center-play-icon">▶</div>
		</div>

		<!-- Индикатор загрузки -->
		<div v-if="isLoading" class="vp-custom-loading-overlay">
			<div class="vp-custom-loading-spinner"></div>
		</div>

		<div v-if="hasError" class="vp-custom-error-overlay">
			Ошибка загрузки
		</div>
	</div>
	</teleport>
	</div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'

export default {
	name: 'CustomVideoPlayer',
	props: {
		// URL видео
		videoSource: {
			type: String,
			default:
				'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
		},
		// Постер (превью) видео
		posterImage: {
			type: String,
			default: '',
		},
		// Автовоспроизведение
		autoplay: {
			type: Boolean,
			default: false,
		},
		currentTime: {
			type: Number,
			default: 0,
		},
		onTimeUpdate: {
			type: Function,
			default: null,
		},
		onEnded: {
			type: Function,
			default: null,
		},
		onPlay: {
			type: Function,
			default: null,
		},
		onPause: {
			type: Function,
			default: null,
		},
		onLoadedMetadata: {
			type: Function,
			default: null,
		},
		customFullscreenHandler: {
			type: Function,
			default: null,
		},
		preferEmbeddedFullscreen: {
			type: Boolean,
			default: true,
		},
	},
	setup(props) {
		// Ссылки на элементы
		const playerShell = ref(null)
		const playerWrapper = ref(null)
		const videoElement = ref(null)

		// Состояние плеера
		const isPlaying = ref(false)
		const isLoading = ref(true)
		const hasError = ref(false)
		const isMuted = ref(false)
		const isFullscreen = ref(false)
		const isEmbeddedFullscreen = ref(false)
		const showControls = ref(true)
		const currentTime = ref(0)
		const duration = ref(0)
		const volume = ref(1)
		const embeddedPlaceholderHeight = ref(0)

		// Таймер для скрытия контролов
		let controlsTimeout = null
		let previousHtmlOverflow = ''
		let previousBodyOverflow = ''
		let exitScrollGuardRafId = null
		let exitScrollGuardTimerId = null
		let scrollRestoreX = 0
		let scrollRestoreY = 0

		watch(
			() => props.videoSource,
			(newSource, oldSource) => {
				if (!videoElement.value || newSource === oldSource) return
				setEmbeddedFullscreen(false)
				videoElement.value.pause()
				isPlaying.value = false
				currentTime.value = 0
				duration.value = 0
				hasError.value = false
				isLoading.value = Boolean(newSource)
				videoElement.value.src = newSource || ''
				videoElement.value.load()
			}
		)

		// Вычисляемые свойства
		const progressPercent = computed(() => {
			if (duration.value === 0) return 0
			return (currentTime.value / duration.value) * 100
		})

		const shellPlaceholderStyle = computed(() => {
			if (!isEmbeddedFullscreen.value || !embeddedPlaceholderHeight.value) return null
			return {
				height: `${embeddedPlaceholderHeight.value}px`,
			}
		})

		/**
		 * Форматирование времени в формат MM:SS
		 */
		const formatTime = seconds => {
			if (isNaN(seconds)) return '00:00'
			const mins = Math.floor(seconds / 60)
			const secs = Math.floor(seconds % 60)
			return `${mins.toString().padStart(2, '0')}:${secs
				.toString()
				.padStart(2, '0')}`
		}

		/**
		 * Переключение воспроизведения/паузы
		 */
		const togglePlay = async () => {
			if (!videoElement.value) return

			if (!videoElement.value.paused && !videoElement.value.ended) {
				videoElement.value.pause()
				return
			}

			try {
				const playPromise = videoElement.value.play()
				if (playPromise && typeof playPromise.then === 'function') {
					await playPromise
				}
			} catch (error) {
				isPlaying.value = false
			}
		}

		/**
		 * Обновление прогресса воспроизведения
		 */
		const updateProgress = () => {
			if (!videoElement.value) return
			const newTime = videoElement.value.currentTime
			currentTime.value = newTime
			if (typeof props.onTimeUpdate === 'function') {
				props.onTimeUpdate(newTime, duration.value)
			}
		}

		/**
		 * Перемотка видео по клику на прогресс-бар
		 */
		const seek = event => {
			if (!videoElement.value) return
			const rect = event.currentTarget.getBoundingClientRect()
			const percent = (event.clientX - rect.left) / rect.width
			videoElement.value.currentTime = percent * duration.value
		}

		/**
		 * Переключение звука
		 */
		const toggleMute = () => {
			if (!videoElement.value) return
			videoElement.value.muted = !videoElement.value.muted
			isMuted.value = videoElement.value.muted
		}

		/**
		 * Изменение громкости
		 */
		const changeVolume = event => {
			if (!videoElement.value) return
			const newVolume = event.target.value / 100
			videoElement.value.volume = newVolume
			volume.value = newVolume
			isMuted.value = newVolume === 0
		}

		const applyEmbeddedFullscreenDocumentState = active => {
			if (typeof document === 'undefined') return

			const html = document.documentElement
			const body = document.body
			if (!html || !body) return

			if (active) {
				previousHtmlOverflow = html.style.overflow
				previousBodyOverflow = body.style.overflow
				html.style.overflow = 'hidden'
				body.style.overflow = 'hidden'
				return
			}

			html.style.overflow = previousHtmlOverflow
			body.style.overflow = previousBodyOverflow
		}

		const captureScrollPosition = () => {
			if (typeof window === 'undefined') return
			scrollRestoreX = window.scrollX || 0
			scrollRestoreY = window.scrollY || 0
		}

		const restoreScrollPosition = () => {
			if (typeof window === 'undefined') return
			window.scrollTo({
				left: scrollRestoreX,
				top: scrollRestoreY,
				behavior: 'auto',
			})
		}

		const clearExitScrollGuard = () => {
			if (typeof window === 'undefined') return
			if (exitScrollGuardRafId) {
				window.cancelAnimationFrame(exitScrollGuardRafId)
				exitScrollGuardRafId = null
			}
			if (exitScrollGuardTimerId) {
				window.clearTimeout(exitScrollGuardTimerId)
				exitScrollGuardTimerId = null
			}
		}

		const runExitScrollGuard = deadline => {
			restoreScrollPosition()
			if (Date.now() >= deadline || typeof window === 'undefined') {
				clearExitScrollGuard()
				return
			}
			exitScrollGuardRafId = window.requestAnimationFrame(() => {
				runExitScrollGuard(deadline)
			})
		}

		const startExitScrollGuard = durationMs => {
			if (typeof window === 'undefined') return
			clearExitScrollGuard()
			const deadline = Date.now() + durationMs
			runExitScrollGuard(deadline)
			exitScrollGuardTimerId = window.setTimeout(() => {
				clearExitScrollGuard()
			}, durationMs + 120)
		}

		const setEmbeddedFullscreen = active => {
			if (isEmbeddedFullscreen.value === active) return

			if (active) {
				captureScrollPosition()
				const shellHeight =
					playerShell.value?.getBoundingClientRect?.().height ||
					playerWrapper.value?.getBoundingClientRect?.().height ||
					0
				embeddedPlaceholderHeight.value = Math.max(Math.round(shellHeight), 1)
			}

			isEmbeddedFullscreen.value = active
			isFullscreen.value = active
			applyEmbeddedFullscreenDocumentState(active)
			showControlsTemporarily()

			if (!active) {
				startExitScrollGuard(1000)
				nextTick(() => {
					embeddedPlaceholderHeight.value = 0
					restoreScrollPosition()
				})
			}
		}

		const handleEmbeddedFullscreenKeydown = event => {
			if (event.key === 'Escape' && isEmbeddedFullscreen.value) {
				event.preventDefault()
				setEmbeddedFullscreen(false)
			}
		}

		/**
		 * Переключение полноэкранного режима
		 */
		const syncFullscreenState = () => {
			const fullscreenElement =
				document.fullscreenElement ||
				document.webkitFullscreenElement ||
				document.msFullscreenElement ||
				null

			isFullscreen.value = Boolean(
				fullscreenElement &&
					(playerWrapper.value === fullscreenElement ||
						videoElement.value === fullscreenElement)
			)
		}

		const toggleFullscreen = async () => {
			if (typeof props.customFullscreenHandler === 'function') {
				props.customFullscreenHandler({
					videoElement: videoElement.value,
					playerWrapper: playerWrapper.value,
				})
				return
			}

			// Всегда используем встроенный fullscreen-оверлей компонента,
			// чтобы не триггерить системный fullscreen браузера и автоскролл страницы.
			setEmbeddedFullscreen(!isEmbeddedFullscreen.value)
		}

		/**
		 * Обработчик загрузки метаданных видео
		 */
		const onVideoLoaded = () => {
			if (!videoElement.value) return
			duration.value = videoElement.value.duration
			isLoading.value = false
			hasError.value = false

			if (typeof props.onLoadedMetadata === 'function') {
				props.onLoadedMetadata(duration.value)
			}

			// Автовоспроизведение
			if (props.autoplay) {
				togglePlay()
			}
		}

		const onPlay = () => {
			isPlaying.value = true
			if (typeof props.onPlay === 'function') {
				props.onPlay()
			}
		}

		const onPause = () => {
			isPlaying.value = false
			if (typeof props.onPause === 'function') {
				props.onPause()
			}
		}

		/**
		 * Обработчик окончания видео
		 */
		const onVideoEnded = () => {
			isPlaying.value = false
			currentTime.value = 0
			if (videoElement.value) {
				videoElement.value.currentTime = 0
			}
			if (typeof props.onEnded === 'function') {
				props.onEnded()
			}
		}
		const onVideoError = () => {
			isLoading.value = false
			isPlaying.value = false
			hasError.value = true
		}


		/**
		 * Показать контролы
		 */
		const showControlsTemporarily = () => {
			showControls.value = true

			// Очистить предыдущий таймер
			if (controlsTimeout) {
				clearTimeout(controlsTimeout)
			}

			// Скрыть контролы через 3 секунды
			if (isPlaying.value) {
				controlsTimeout = setTimeout(() => {
					showControls.value = false
				}, 3000)
			}
		}

		// Lifecycle hooks
		onMounted(() => {
			isLoading.value = Boolean(props.videoSource)
			// Показывать контролы при движении мыши
			const container = playerWrapper.value || videoElement.value?.parentElement
			if (container) {
				container.addEventListener('mousemove', showControlsTemporarily)
				container.addEventListener('mouseleave', () => {
					if (isPlaying.value) {
						showControls.value = false
					}
				})
			}

			document.addEventListener('fullscreenchange', syncFullscreenState)
			document.addEventListener('webkitfullscreenchange', syncFullscreenState)
			document.addEventListener('msfullscreenchange', syncFullscreenState)
			document.addEventListener('keydown', handleEmbeddedFullscreenKeydown)
			videoElement.value?.addEventListener('webkitbeginfullscreen', syncFullscreenState)
			videoElement.value?.addEventListener('webkitendfullscreen', syncFullscreenState)
		})

		onBeforeUnmount(() => {
			setEmbeddedFullscreen(false)
			clearExitScrollGuard()

			if (controlsTimeout) {
				clearTimeout(controlsTimeout)
			}

			document.removeEventListener('fullscreenchange', syncFullscreenState)
			document.removeEventListener('webkitfullscreenchange', syncFullscreenState)
			document.removeEventListener('msfullscreenchange', syncFullscreenState)
			document.removeEventListener('keydown', handleEmbeddedFullscreenKeydown)
			videoElement.value?.removeEventListener(
				'webkitbeginfullscreen',
				syncFullscreenState
			)
			videoElement.value?.removeEventListener(
				'webkitendfullscreen',
				syncFullscreenState
			)
		})

		return {
			playerShell,
			playerWrapper,
			videoElement,
			isPlaying,
			isLoading,
			isMuted,
			isFullscreen,
			isEmbeddedFullscreen,
			showControls,
			currentTime,
			duration,
			volume,
			hasError,
			progressPercent,
			shellPlaceholderStyle,
			formatTime,
			togglePlay,
			updateProgress,
			seek,
			toggleMute,
			changeVolume,
			toggleFullscreen,
			onVideoLoaded,
			onPlay,
			onPause,
			onVideoEnded,
			onVideoError,
		}
	},
}
</script>

<style scoped>
.vp-custom-player-shell {
	position: relative;
	width: 100%;
	height: 100%;
}
/* Контейнер видеоплеера */
.vp-custom-player-wrapper {
	position: relative;
	width: 100%;
	height: 100%;
	/* background-color: #000; */
	overflow: hidden;
	border-radius: 12px;
}

/* Видео элемент */
.vp-custom-video-element {
	width: 100%;
	height: 100%;
	object-fit: cover;
	cursor: pointer;
}

.vp-custom-player-wrapper:fullscreen,
.vp-custom-player-wrapper:-webkit-full-screen,
.vp-custom-player-wrapper:-ms-fullscreen {
	width: 100vw;
	height: 100vh;
	background: #000;
	border-radius: 0;
}

.vp-custom-player-wrapper:fullscreen .vp-custom-video-element,
.vp-custom-player-wrapper:-webkit-full-screen .vp-custom-video-element,
.vp-custom-player-wrapper:-ms-fullscreen .vp-custom-video-element {
	object-fit: contain;
}

.vp-custom-player-wrapper--embedded-fullscreen {
	position: fixed;
	inset: 0;
	width: 100dvw;
	height: 100dvh;
	background: #000;
	border-radius: 0;
	z-index: 110000;
}

.vp-custom-player-wrapper--embedded-fullscreen .vp-custom-video-element {
	object-fit: contain;
}

/* Контролы видео */
.vp-custom-controls-overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
	padding: 20px 15px 15px;
	opacity: 0;
	transition: opacity 0.3s ease;
}

.vp-custom-controls-overlay.vp-custom-controls-visible {
	opacity: 1;
}

.vp-custom-player-wrapper:hover .vp-custom-controls-overlay {
	opacity: 1;
}

/* Прогресс-бар */
.vp-custom-progress-wrapper {
	width: 100%;
	height: 20px;
	cursor: pointer;
	display: flex;
	align-items: center;
	margin-bottom: 10px;
}

.vp-custom-progress-track {
	position: relative;
	width: 100%;
	height: 4px;
	background-color: rgba(255, 255, 255, 0.3);
	border-radius: 2px;
	overflow: visible;
}

.vp-custom-progress-fill {
	height: 100%;
	background-color: #0095ec;
	border-radius: 2px;
	transition: width 0.1s linear;
}

.vp-custom-progress-thumb {
	position: absolute;
	top: 50%;
	transform: translate(-50%, -50%);
	width: 12px;
	height: 12px;
	background-color: #0095ec;
	border-radius: 50%;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.vp-custom-progress-wrapper:hover .vp-custom-progress-thumb {
	opacity: 1;
}

/* Нижняя панель контролов */
.vp-custom-controls-panel {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 15px;
}

.vp-custom-controls-left-group,
.vp-custom-controls-right-group {
	display: flex;
	align-items: center;
	gap: 10px;
}

/* Кнопки управления */
.vp-custom-btn {
	background: none;
	border: none;
	color: white;
	cursor: pointer;
	padding: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.2s ease, opacity 0.2s ease;
	font-size: 18px;
}

.vp-custom-btn:hover {
	transform: scale(1.1);
	opacity: 0.8;
}

.vp-custom-btn .vp-custom-icon {
	display: block;
	line-height: 1;
}

/* Отображение времени */
.vp-custom-time-info {
	color: white;
	font-size: 14px;
	font-family: 'Poppins', sans-serif;
	display: flex;
	gap: 5px;
	user-select: none;
}

/* Контрол громкости */
.vp-custom-volume-section {
	display: flex;
	align-items: center;
	gap: 8px;
}

.vp-custom-volume-slider-wrapper {
	width: 0;
	overflow: hidden;
	transition: width 0.3s ease;
  padding-right: 2px;
  margin-bottom: 9px;
}

.vp-custom-volume-section:hover .vp-custom-volume-slider-wrapper {
	width: 80px;
}

.vp-custom-volume-range {
	width: 80px;
	height: 4px;
	-webkit-appearance: none;
	appearance: none;
	background: rgba(255, 255, 255, 0.3);
	border-radius: 2px;
  padding-right: 2.5px;
	outline: none;
	cursor: pointer;
}

.vp-custom-volume-range::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 11px;
	height: 11px;
	background: #0095ec;
	border-radius: 50%;
	cursor: pointer;
}

.vp-custom-volume-range::-moz-range-thumb {
	width: 11px;
	height: 11px;
	background: #0095ec;
	border-radius: 50%;
	cursor: pointer;
	border: none;
}

/* Центральная кнопка Play */
/* Центральная кнопка Play */
.vp-custom-center-play-overlay {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 80px;
	height: 80px;
	background-color: rgba(0, 149, 236, 0.9);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.vp-custom-center-play-overlay:hover {
	transform: translate(-50%, -50%) scale(1.1);
	background-color: rgba(0, 149, 236, 1);
}

.vp-custom-center-play-icon {
	color: white;
	font-size: 32px;
	margin-left: 5px;
}

/* Индикатор загрузки */
.vp-custom-loading-overlay {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

.vp-custom-loading-spinner {
	width: 50px;
	height: 50px;
	border: 4px solid rgba(255, 255, 255, 0.3);
	border-top-color: #0095ec;
	border-radius: 50%;
	animation: vp-custom-spin-animation 1s linear infinite;
}

.vp-custom-error-overlay {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(0, 0, 0, 0.7);
	color: #ffffff;
	padding: 8px 14px;
	border-radius: 8px;
	font-family: 'Poppins', sans-serif;
	font-size: 14px;
}

@keyframes vp-custom-spin-animation {
	to {
		transform: rotate(360deg);
	}
}

/* Адаптивность */
@media (max-width: 768px) {
	.vp-custom-time-info {
		font-size: 12px;
	}

	.vp-custom-btn {
		font-size: 16px;
	}

	.vp-custom-center-play-overlay {
		width: 60px;
		height: 60px;
	}

	.vp-custom-center-play-icon {
		font-size: 24px;
	}
}
</style>
